import React from 'react';
import { styled } from '@mui/material/styles';
import { Button as MuiButton, ButtonProps as MuiButtonProps } from '@mui/material';

const StyledButton = styled(MuiButton)<{
	customvariant?: 'contained' | 'outlined' | 'text' | 'destructive';
	icononly?: string;
	buttonsize?: 'small' | 'medium' | 'large';
}>(({ theme: _theme, customvariant: variant = 'outlined', icononly, buttonsize = 'medium' }) => {
	const sizeConfig = {
		small: {
			fontSize: '12px',
			padding: icononly === 'true' ? '4px' : '4px 10px',
			minWidth: icononly === 'true' ? '28px' : 'auto',
			height: '28px',
		},
		medium: {
			fontSize: '14px',
			padding: icononly === 'true' ? '6px' : '6px 16px',
			minWidth: icononly === 'true' ? '32px' : 'auto',
			height: '32px',
		},
		large: {
			fontSize: '16px',
			padding: icononly === 'true' ? '8px' : '8px 22px',
			minWidth: icononly === 'true' ? '40px' : 'auto',
			height: '40px',
		},
	};

	const currentSize = sizeConfig[buttonsize];

	return {
		borderRadius: '8px',
		padding: currentSize.padding,
		minWidth: currentSize.minWidth,
		height: currentSize.height,
		boxSizing: 'border-box',
		textTransform: 'none',
		fontWeight: 500,
		fontSize: currentSize.fontSize,
		transition: 'all 0.2s ease',
		'& svg': {
			width: '16px',
			height: '16px',
		},
		...(icononly === 'true' && {
			'& .MuiButton-startIcon': {
				margin: 0,
			},
		}),

		// Outlined variant styles
		...(variant === 'outlined' && {
			border: '1px solid var(--neutral-outline)',
			backgroundColor: 'var(--neutral-base-white)',
			color: 'var(--neutral-dark-base)',
			'&:hover': {
				backgroundColor: 'var(--primary-100)',
				borderColor: 'var(--neutral-outline)',
			},
			'&.active': {
				backgroundColor: 'var(--primary-200)',
				borderColor: 'var(--primary-500)',
			},
			'&.Mui-disabled': {
				backgroundColor: 'var(--neutral-base-white)',
				color: 'var(--neutral-gray-disabled)',
				borderColor: 'var(--neutral-outline)',
			},
			'& .MuiTouchRipple-root .MuiTouchRipple-child': {
				backgroundColor: 'var(--primary-300) !important',
			},
		}),

		...(variant === 'contained' && {
			backgroundColor: 'var(--primary-500)',
			color: 'var(--neutral-base-white)',
			border: 'none',
			boxShadow: '0px 2px 4px -1px #00000033, 0px 4px 5px 0px #00000024, 0px 1px 10px 0px #0000001F',
			'&:hover': {
				backgroundColor: 'var(--primary-600)',
				boxShadow: '0px 3px 5px -1px #00000033, 0px 6px 10px 0px #00000024, 0px 1px 18px 0px #0000001F',
			},
			'&:active': {
				backgroundColor: 'var(--primary-600)',
				boxShadow: '0px 3px 5px -1px #00000033, 0px 6px 10px 0px #00000024, 0px 1px 18px 0px #0000001F',
			},
			'&.Mui-disabled': {
				backgroundColor: 'var(--neutral-bg-light-colored)',
				color: 'var(--neutral-gray-disabled)',
			},
			'& .MuiTouchRipple-root .MuiTouchRipple-child': {
				backgroundColor: 'rgba(255, 255, 255, 0.3) !important',
			},
		}),

		...(variant === 'text' && {
			backgroundColor: 'transparent',
			color: 'var(--neutral-dark-base)',
			border: 'none',
			'&:hover': {
				backgroundColor: 'var(--primary-100)',
			},
			'&:active': {
				backgroundColor: 'var(--primary-200)',
			},
			'& .MuiTouchRipple-root .MuiTouchRipple-child': {
				backgroundColor: 'var(--primary-200) !important',
			},
		}),

		...(variant === 'destructive' && {
			background: 'var(--error-500)',
			color: 'var(--neutral-base-white)',
			border: 'none',
			boxShadow: '0px 2px 4px -1px #00000033, 0px 4px 5px 0px #00000024, 0px 1px 10px 0px #0000001F',
			'&:hover': {
				background: 'linear-gradient(0deg, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.1)), var(--error-500)',
				boxShadow: '0px 3px 5px -1px #00000033, 0px 6px 10px 0px #00000024, 0px 1px 18px 0px #0000001F',
			},
			'&:active': {
				background: 'linear-gradient(0deg, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.1)), var(--error-500)',
				boxShadow: '0px 3px 5px -1px #00000033, 0px 6px 10px 0px #00000024, 0px 1px 18px 0px #0000001F',
			},
			'&.Mui-disabled': {
				background: 'var(--neutral-bg-light-colored)',
				color: 'var(--neutral-gray-disabled)',
			},
			'& .MuiTouchRipple-root .MuiTouchRipple-child': {
				backgroundColor: 'rgba(255, 255, 255, 0.3) !important',
			},
		}),
	};
});

interface ButtonProps extends Omit<MuiButtonProps, 'variant' | 'size'> {
	variant?: 'contained' | 'outlined' | 'text' | 'destructive';
	size?: 'small' | 'medium' | 'large';
	icon?: React.ReactNode;
	text?: string;
}

export const Button: React.FC<ButtonProps> = ({
	variant = 'outlined',
	size = 'medium',
	icon,
	text,
	className = '',
	children,
	...props
}) => {
	const isIconOnly = icon && !text && !children;
	const muiVariant = variant === 'destructive' ? 'contained' : variant;

	return (
		<StyledButton
			variant={muiVariant}
			customvariant={variant}
			className={className}
			startIcon={icon}
			icononly={isIconOnly ? 'true' : 'false'}
			buttonsize={size}
			{...props}
		>
			{text || children}
		</StyledButton>
	);
};

export default Button;
